/**
 * Listing Wizard Selectors
 * 
 * SOLID Principles Applied:
 * - Single Responsibility: Only handles data selection from Redux state
 * - Separation of Concerns: Isolated from state management and async operations
 * - Open/Closed: Easy to add new selectors without modifying existing ones
 * 
 * This file contains all selectors for accessing listing wizard state
 */

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { isValidListingSlug } from '../../../pages/seller/ListingWizard/utils/listingSlugUtils';

// ============================================
// BASE SELECTORS
// ============================================

/**
 * Select the entire listing wizard state
 */
const selectListingWizardState = (state: RootState) => state.listingWizard;

// ============================================
// DERIVED SELECTORS
// ============================================

/**
 * Select design data
 */
export const selectDesign = createSelector(
  [selectListingWizardState],
  (wizard) => wizard.design
);

/**
 * Select available products
 */
export const selectAvailableProducts = createSelector(
  [selectListingWizardState],
  (wizard) => wizard.availableProducts
);

/**
 * Select current listing
 */
export const selectCurrentListing = createSelector(
  [selectListingWizardState],
  (wizard) => wizard.currentListing
);

/**
 * Market taxonomy + discovery tag vocabulary from the wizard snapshot (API source of truth).
 */
export const selectListingMarketTaxonomy = createSelector(
  [selectListingWizardState],
  (wizard) => wizard.marketTaxonomy
);

/**
 * Select current step
 */
export const selectCurrentStep = createSelector(
  [selectListingWizardState],
  (wizard) => wizard.currentStep
);

/**
 * Select completed sections
 */
export const selectCompletedSections = createSelector(
  [selectListingWizardState],
  (wizard) => wizard.completedSections
);

/**
 * Select saved data for all sections
 */
export const selectSavedData = createSelector(
  [selectListingWizardState],
  (wizard) => wizard.savedData
);

/**
 * Select saved data for a specific section
 */
export const selectSectionData = (section: string) =>
  createSelector([selectSavedData], (savedData) => savedData[section] || null);

/**
 * Select loading states
 */
export const selectIsLoadingSnapshot = createSelector(
  [selectListingWizardState],
  (wizard) => wizard.isLoadingSnapshot
);

export const selectIsSaving = createSelector(
  [selectListingWizardState],
  (wizard) => wizard.isSaving
);

export const selectIsPublishing = createSelector(
  [selectListingWizardState],
  (wizard) => wizard.isPublishing
);

/**
 * Select error state
 */
export const selectError = createSelector(
  [selectListingWizardState],
  (wizard) => wizard.error
);

/**
 * Check if a specific section is completed
 */
export const selectIsSectionCompleted = (section: string) =>
  createSelector([selectCompletedSections], (completed) =>
    completed.includes(section as any)
  );

/**
 * Calculate wizard progress (percentage)
 */
export const selectWizardProgress = createSelector(
  [selectCompletedSections],
  (completed) => {
    const totalSections = 7; // Step 0 is intro, Steps 1-7 are actual sections
    return Math.round((completed.length / totalSections) * 100);
  }
);

/**
 * Check if wizard can be published (all required sections + market/SEO quality gates)
 */
export const selectCanPublish = createSelector(
  [selectCompletedSections, selectSavedData],
  (completed, savedData) => {
    const requiredSections: string[] = [
      'identity',
      'catalog',
      'visuals',
      'pricing',
      'seo',
    ];
    if (!requiredSections.every((section) => completed.includes(section as any))) {
      return false;
    }

    const rawIdentity = savedData.identity as
      | Partial<{
          primaryMarketNicheKey: string;
          niche: string;
          productCategoryKeys: string[];
          productTypeKeys: string[];
          productCategoryKey: string;
          productTypeKey: string;
          audienceKeys: string[];
          audience: string[];
        }>
      | undefined;
    const primaryMarketNicheKey = (
      rawIdentity?.primaryMarketNicheKey ??
      rawIdentity?.niche ??
      ''
    ).trim();
    if (!primaryMarketNicheKey) {
      return false;
    }

    const hasProductCategories =
      (Array.isArray(rawIdentity?.productCategoryKeys) &&
        rawIdentity!.productCategoryKeys!.some((x) => String(x).trim())) ||
      Boolean(String(rawIdentity?.productCategoryKey ?? '').trim());
    const hasProductTypes =
      (Array.isArray(rawIdentity?.productTypeKeys) &&
        rawIdentity!.productTypeKeys!.some((x) => String(x).trim())) ||
      Boolean(String(rawIdentity?.productTypeKey ?? '').trim());
    if (!hasProductCategories || !hasProductTypes) {
      return false;
    }

    const hasAudience =
      (Array.isArray(rawIdentity?.audienceKeys) &&
        rawIdentity!.audienceKeys!.some((x) => String(x).trim())) ||
      (Array.isArray(rawIdentity?.audience) &&
        rawIdentity!.audience!.some((x) => String(x).trim()));
    if (!hasAudience) {
      return false;
    }

    const seo = savedData.seo as
      | { tags?: string[]; slug?: string; seoDescription?: string }
      | undefined;
    const tags = Array.isArray(seo?.tags) ? seo!.tags! : [];
    const slug = typeof seo?.slug === 'string' ? seo.slug.trim() : '';
    const slugOk = isValidListingSlug(slug);
    if (tags.length < 3 || !slugOk) {
      return false;
    }

    const seoDescription =
      typeof seo?.seoDescription === 'string' ? seo.seoDescription.trim() : '';
    const storySeed =
      typeof (rawIdentity as any)?.story === 'string'
        ? (rawIdentity as any).story.trim()
        : '';
    const subtitleSeed =
      typeof (rawIdentity as any)?.subtitle === 'string'
        ? (rawIdentity as any).subtitle.trim()
        : '';
    const effectiveSeoDescription =
      !seoDescription || (storySeed && subtitleSeed && seoDescription === subtitleSeed)
        ? storySeed
        : seoDescription;
    if (effectiveSeoDescription.length < 80) {
      return false;
    }

    return true;
  }
);

/**
 * Get missing required sections for publishing
 */
export const selectMissingRequiredSections = createSelector(
  [selectCompletedSections, selectSavedData],
  (completed, savedData) => {
    const requiredSections: Array<{ key: string; label: string; step: number }> = [
      { key: 'identity', label: 'Buyer & product fit (title, audience, story)', step: 1 },
      { key: 'catalog', label: 'Products & surfaces', step: 2 },
      { key: 'visuals', label: 'Visuals & mockups', step: 3 },
      { key: 'pricing', label: 'Pricing & earnings', step: 6 },
      { key: 'seo', label: 'Discovery & search', step: 7 },
    ];
    const missing = requiredSections.filter(
      (section) => !completed.includes(section.key as any)
    );

    const rawIdentity = savedData.identity as
      | Partial<{
          primaryMarketNicheKey: string;
          niche: string;
          productCategoryKeys: string[];
          productTypeKeys: string[];
          productCategoryKey: string;
          productTypeKey: string;
          audienceKeys: string[];
          audience: string[];
        }>
      | undefined;
    const primaryMarketNicheKey = (
      rawIdentity?.primaryMarketNicheKey ??
      rawIdentity?.niche ??
      ''
    ).trim();
    if (
      !primaryMarketNicheKey &&
      !missing.some((m) => m.key === 'identity')
    ) {
      missing.push({
        key: 'identity-niche',
        label: 'Main audience (Step 1)',
        step: 1,
      });
    }

    const hasProductCategories =
      (Array.isArray(rawIdentity?.productCategoryKeys) &&
        rawIdentity!.productCategoryKeys!.some((x) => String(x).trim())) ||
      Boolean(String(rawIdentity?.productCategoryKey ?? '').trim());
    const hasProductTypes =
      (Array.isArray(rawIdentity?.productTypeKeys) &&
        rawIdentity!.productTypeKeys!.some((x) => String(x).trim())) ||
      Boolean(String(rawIdentity?.productTypeKey ?? '').trim());
    if (
      (!hasProductCategories || !hasProductTypes) &&
      !missing.some((m) => m.key === 'identity')
    ) {
      missing.push({
        key: 'identity-product',
        label: 'Product family and type (Step 1)',
        step: 1,
      });
    }

    const hasAudience =
      (Array.isArray(rawIdentity?.audienceKeys) &&
        rawIdentity!.audienceKeys!.some((x) => String(x).trim())) ||
      (Array.isArray(rawIdentity?.audience) &&
        rawIdentity!.audience!.some((x) => String(x).trim()));
    if (
      !hasAudience &&
      !missing.some((m) => m.key === 'identity')
    ) {
      missing.push({
        key: 'identity-audience',
        label: 'Who will wear it — pick at least one (Step 1)',
        step: 1,
      });
    }

    const seo = savedData.seo as
      | { tags?: string[]; slug?: string; seoDescription?: string }
      | undefined;
    const tags = Array.isArray(seo?.tags) ? seo.tags : [];
    const slug = typeof seo?.slug === 'string' ? seo.slug.trim() : '';
    const slugOk = isValidListingSlug(slug);
    if (
      completed.includes('seo' as any) &&
      (tags.length < 3 || !slugOk) &&
      !missing.some((m) => m.key === 'seo')
    ) {
      missing.push({
        key: 'seo-quality',
        label: 'At least 3 discovery tags and a valid URL slug (Discovery step)',
        step: 7,
      });
    }

    const seoDescription =
      typeof seo?.seoDescription === 'string' ? seo.seoDescription.trim() : '';
    const storySeed =
      typeof (rawIdentity as any)?.story === 'string'
        ? (rawIdentity as any).story.trim()
        : '';
    const subtitleSeed =
      typeof (rawIdentity as any)?.subtitle === 'string'
        ? (rawIdentity as any).subtitle.trim()
        : '';
    const effectiveSeoDescription =
      !seoDescription || (storySeed && subtitleSeed && seoDescription === subtitleSeed)
        ? storySeed
        : seoDescription;
    if (
      completed.includes('seo' as any) &&
      effectiveSeoDescription.length < 80 &&
      !missing.some((m) => m.key === 'seo')
    ) {
      missing.push({
        key: 'seo-description-depth',
        label: 'Search snippet needs more detail (80+ characters)',
        step: 7,
      });
    }

    return missing;
  }
);

